import React, { useState } from "react";
import { Divider, IconButton, Menu, MenuListProps, PaperProps } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SwapIcon from "@mui/icons-material/SwapHoriz";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";

import { MenuItem } from "@/features/admin/components";
import {
  WidgetOrderDialog,
  useDeleteAllWidgetsMutation,
  useDeleteWidgetMutation,
} from "@/features/admin/features/widgets";
import { usePageData } from "@/features/admin/features/pages-management";
import { ModalComponent, confirm, showToast } from "@/features/shared/components";
import CreateWidgetDialog from "@/features/widget-forms/dialogs/create-widget-dialog";
import { serializeWidgetForClipboard } from "@/features/admin/features/widgets/widget-clipboard";
import { usePasteWidget } from "@/features/admin/features/widgets/hooks/use-paste-widget";
import { Widget } from "@/lib/api";
import { FormValues } from "@/types/widget";
import { UpdateWidgetDialog } from "@/features/widget-forms/dialogs/update-widget-dialog";
import { useTranslation } from "@/lib/i18n";

const EditWidgetContainerMenu = ({
  pageId,
  containerId,
  activeWidgetId,
}: {
  pageId: string;
  containerId: string;
  activeWidgetId: string;
}) => {
  const { t } = useTranslation();
  const { page } = usePageData();

  // Get the latest widget data from the page query to ensure reactivity
  const activeWidget = React.useMemo(() => {
    if (!page) return undefined;
    const container = page.widget_containers.find((c) => c.id === containerId);
    return container?.widgets.find((w) => w.id === activeWidgetId);
  }, [page, containerId, activeWidgetId]);
  const deleteWidgetMutation = useDeleteWidgetMutation();
  const deleteWidgetContainerMutation = useDeleteAllWidgetsMutation();
  const readPastedWidget = usePasteWidget();

  const orderModalRef = React.useRef<ModalComponent>();

  const [createWidgetDialogOpen, setCreateWidgetDialogOpen] = useState(false);
  const [updateWidgetDialogOpen, setUpdateWidgetDialogOpen] = useState(false);
  const [pastedValues, setPastedValues] = useState<FormValues>();
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCopy = async () => {
    handleClose();
    if (!activeWidget) return;

    const container = page?.widget_containers.find((c) => c.id === containerId);
    const layout = container
      ? { width: container.width, height: container.height }
      : undefined;

    try {
      await navigator.clipboard.writeText(
        serializeWidgetForClipboard(activeWidget, layout),
      );
      showToast(t("admin.messages.widget_copied"));
    } catch {
      showToast(t("admin.messages.widget_copy_failed"), { type: "error" });
    }
  };

  const handlePaste = async () => {
    handleClose();
    const parsed = await readPastedWidget();
    if (!parsed) return;

    setPastedValues(parsed.values);
    setPasteDialogOpen(true);
  };

  const handleDelete = async () => {
    const confirmed = await confirm(
      t("admin.widgets.remove_confirm.title"),
      t("admin.widgets.remove_confirm.body"),
    );

    if (!confirmed) return;

    if (!activeWidget) return;

    deleteWidgetMutation.mutate(
      { widgetId: activeWidget.id, pageId },
      {
        onSuccess: () => {
          showToast(t("admin.messages.widget_deleted"));
          setAnchorEl(null);
        },
      },
    );
  };

  const handleDeleteContainer = async () => {
    const confirmed = await confirm(
      t("admin.widgets.remove_container_confirm.title"),
      t("admin.widgets.remove_container_confirm.body"),
    );

    if (!confirmed) return;

    deleteWidgetContainerMutation.mutate(
      {
        pageId,
        widgetContainerId: containerId,
      },
      {
        onSuccess: () => {
          showToast(t("admin.messages.widget_container_deleted"));
          setAnchorEl(null);
        },
      },
    );
  };

  return (
    <div data-no-drag>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={
          {
            // Menu renders in a portal; inherit data-no-drag so RGL won't start a grid drag.
            "data-no-drag": true,
          } as Partial<MenuListProps>
        }
        PaperProps={{ "data-no-drag": true } as Partial<PaperProps>}
      >
        <MenuItem
          title={t("admin.widgets.edit_menu.add_widget")}
          icon={<AddIcon fontSize="small" />}
          onClick={() => {
            handleClose();
            setCreateWidgetDialogOpen(true);
          }}
        />

        <MenuItem
          title={t("admin.widgets.edit_menu.update_widget")}
          icon={<EditIcon fontSize="small" />}
          onClick={() => {
            handleClose();
            setUpdateWidgetDialogOpen(true);
          }}
        />
        <MenuItem
          title={t("admin.widgets.edit_menu.remove_widget")}
          icon={<DeleteIcon fontSize="small" />}
          onClick={handleDelete}
        />
        <Divider />
        <MenuItem
          title={t("admin.widgets.edit_menu.copy_widget")}
          icon={<ContentCopyIcon fontSize="small" />}
          onClick={handleCopy}
        />
        <MenuItem
          title={t("admin.widgets.edit_menu.paste_widget")}
          icon={<ContentPasteIcon fontSize="small" />}
          onClick={handlePaste}
        />
        <MenuItem
          title={t("admin.widgets.edit_menu.widget_order")}
          icon={<SwapIcon fontSize="small" />}
          onClick={() => orderModalRef.current?.open?.()}
        />
        <Divider />
        <MenuItem
          title={t("admin.widgets.edit_menu.remove_container")}
          color="error"
          icon={<DeleteForeverIcon fontSize="small" color="error" />}
          onClick={handleDeleteContainer}
        />
      </Menu>

      <CreateWidgetDialog
        pageId={pageId}
        containerId={containerId}
        open={createWidgetDialogOpen}
        onOpenChange={(value) => setCreateWidgetDialogOpen(value)}
      />
      {pastedValues && (
        <CreateWidgetDialog
          pageId={pageId}
          containerId={containerId}
          pastedValues={pastedValues}
          open={pasteDialogOpen}
          onOpenChange={(value) => {
            setPasteDialogOpen(value);
            if (!value) setPastedValues(undefined);
          }}
        />
      )}
      {activeWidget && (
        <UpdateWidgetDialog
          key={activeWidget.id}
          initialValues={activeWidget as Required<Widget>}
          widgetId={activeWidget.id}
          pageId={pageId}
          open={updateWidgetDialogOpen}
          onOpenChange={setUpdateWidgetDialogOpen}
          onClose={() => setAnchorEl(null)}
        />
      )}
      <WidgetOrderDialog
        ref={orderModalRef}
        pageId={pageId}
        containerId={containerId}
        onClose={() => setAnchorEl(null)}
      />
    </div>
  );
};

export default EditWidgetContainerMenu;
